import DataHandler from './DataHandler'
import SentryClient from '../utils/SentryClient'
import WebAPIClient from './WebAPIClient'
import { encryptToken, decryptToken, isEncryptedToken } from '../utils/TokenEncryption'

import io from 'socket.io-client'
import { WebSocket, Fetch } from 'engine.io-client'


export default class Network {

    constructor(game) {
        this.game = game

        this.events = new Phaser.Events.EventEmitter()

        this.handler = new DataHandler(this)
        this.client = null

        this.saveUsername = false
        this.savePassword = false
        this.token = null

        // Track reconnection state to manage prompts properly
        this.isShowingReconnectionPrompt = false
        this.isReconnecting = false // Track if we're in a reconnection flow
        this.hasConnectedBefore = false // Track if this is the first connection or a reconnection

        // Used to switch back to correct login scene on an error
        this.lastLoginScene = null

        this.worldName
        
        // Store connection data for reconnection
        this.connectionData = null
        
        // Track authentication state to prevent packets before auth
        this.isAuthenticated = false
        this.pendingPackets = []
        
        // WebAPI client for authentication (required)
        this.webapi = null
        this.useWebAPI = true // Always use WebAPI - no legacy fallback
    }
    
    /**
     * Initialize WebAPI client with config (required)
     * @param {Object} config - WebAPI configuration { url, enabled }
     */
    initWebAPI(config) {
        if (!config || !config.enabled) {
            console.error('[Network] WebAPI config missing or disabled:', config)
            throw new Error('WebAPI configuration is required. Please ensure config.json has webapi.enabled=true and webapi.url set.')
        }
        
        if (!config.url) {
            console.error('[Network] WebAPI URL is missing in config:', config)
            throw new Error('WebAPI URL is required. Please set webapi.url in config.json')
        }
        
        this.webapi = new WebAPIClient(config.url)
        this.useWebAPI = true
        console.log('🌐 WebAPI client initialized:', config.url)
    }
    
    /**
     * WebAPI Login - authenticate via HTTP REST API
     * @param {string} username 
     * @param {string} password 
     * @returns {Promise<Object>} Login result
     */
    async webAPILogin(username, password) {
        console.log('[Network] webAPILogin called for:', username)
        
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_login_attempt', { username })
        
        const result = await this.webapi.login(username, password)
        console.log('[Network] webAPILogin result:', result)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_login_success', { username })
            
            // Traditional Club Penguin flow: Always emit login event with profile
            console.log('[Network] Emitting login event with profile')
            this.events.emit('login', {
                success: true,
                username: result.profile.username,
                profile: result.profile,
                sessionKey: result.sessionKey,
                isWebAPI: true
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_login_failed', { 
                username, 
                error: result.error 
            })
            this.events.emit('login', {
                success: false,
                message: result.error
            })
        }
        
        return result
    }
    
    /**
     * WebAPI Register - create account via HTTP REST API
     * @param {Object} userData - { username, password, email, color }
     * @returns {Promise<Object>} Registration result
     */
    async webAPIRegister(userData) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_register_attempt', { 
            username: userData.username 
        })
        
        const result = await this.webapi.register(userData)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_register_success', { 
                username: userData.username 
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_register_failed', { 
                username: userData.username,
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * WebAPI Register Parent Account - create parent/master account
     * NEW ARCHITECTURE: Parent accounts manage penguins but are not playable
     * @param {Object} parentData - { email, password, isOver13, acceptedTerms, acceptedPrivacy }
     * @returns {Promise<Object>} { success, parentId, email, error }
     */
    async webAPIRegisterParent(parentData) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_register_parent_attempt', { 
            email: parentData.email 
        })
        
        const result = await this.webapi.registerParent(parentData)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_register_parent_success', { 
                email: parentData.email 
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_register_parent_failed', { 
                email: parentData.email,
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * Fetch a server-signed child CAPTCHA challenge (CHILD/TEEN ageBand).
     * Returns signed token + item positions for the counting puzzle.
     */
    async webAPIChildCaptchaChallenge() {
        if (!this.webapi) return null
        return this.webapi.childCaptchaChallenge()
    }

    /**
     * Check if a penguin username is available before completing registration.
     * @param {string} username - Username to check
     * @returns {Promise<Object>} { available, error }
     */
    async webAPICheckUsername(username) {
        if (!this.webapi) return { available: false, error: 'WebAPI not initialized' }
        return this.webapi.checkUsernameAvailability(username)
    }

    /**
     * WebAPI Create Penguin - create a penguin under a parent account
     * NEW ARCHITECTURE: Each penguin has its own username + password
     * @param {Object} penguinData - { parentId, username, password, color, chatType, isUnder13 }
     * @returns {Promise<Object>} { success, penguin, error }
     */
    async webAPICreatePenguin(penguinData) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_create_penguin_attempt', { 
            username: penguinData.username 
        })
        
        const result = await this.webapi.createPenguinWithAuth(penguinData)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_create_penguin_success', { 
                username: penguinData.username 
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_create_penguin_failed', { 
                username: penguinData.username,
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * WebAPI Penguin Login - login with penguin credentials (username + penguin password)
     * NEW ARCHITECTURE: Main login endpoint for playing the game
     * @param {string} username - Penguin username
     * @param {string} password - Penguin password (not parent account password)
     * @returns {Promise<Object>} { success, sessionKey, profile, error }
     */
    async webAPIPenguinLogin(username, password) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_penguin_login_attempt', { username })
        
        const result = await this.webapi.penguinLogin(username, password)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_penguin_login_success', { username })
            
            // Emit login event for game flow
            this.events.emit('login', {
                success: true,
                username: result.profile.username,
                profile: result.profile,
                sessionKey: result.sessionKey,
                isWebAPI: true
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_penguin_login_failed', { 
                username, 
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * WebAPI Update Username - change a penguin's username (for denied usernames)
     * @param {string} penguinId - The penguin ID
     * @param {string} newUsername - The new username to request
     * @returns {Promise<Object>} Update result
     */
    async webAPIUpdateUsername(penguinId, newUsername) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_update_username_attempt', { 
            penguinId: penguinId,
            newUsername: newUsername
        })
        
        const result = await this.webapi.updateUsername(penguinId, newUsername)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_update_username_success', { 
                penguinId: penguinId,
                newUsername: newUsername
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_update_username_failed', { 
                penguinId: penguinId,
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * WebAPI Parent Login - login to parent dashboard with email + password
     * Used when existing account needs to add more penguins
     * @param {Object} credentials - { email, password }
     * @returns {Promise<Object>} { success, parentAccount, penguins, error }
     */
    async webAPIParentLogin(credentials) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_parent_login_attempt', { 
            email: credentials.email 
        })
        
        const result = await this.webapi.parentDashboardLogin(credentials.email, credentials.password)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_parent_login_success', { 
                email: credentials.email
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_parent_login_failed', { 
                email: credentials.email,
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * WebAPI Resend Verification - resend email verification link
     * @param {Object} data - { email }
     * @returns {Promise<Object>} { success, message, error }
     */
    async webAPIResendVerification(data) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        try {
            const response = await this.webapi.request('/auth/resend-verification', {
                method: 'POST',
                body: data,
                skipAuth: true
            })
            return { success: true, message: response.message || 'Verification email sent' }
        } catch (error) {
            return { success: false, error: error.message || 'Failed to resend verification' }
        }
    }
    
    /**
     * WebAPI Refresh Login - authenticate using a stored refresh token
     * Used for "Remember my password" feature
     * @param {string} refreshToken - Stored refresh token
     * @returns {Promise<Object>} Login result
     */
    async webAPIRefreshLogin(refreshToken) {
        if (!this.webapi) {
            return { success: false, error: 'WebAPI not initialized' }
        }
        
        SentryClient.trackNetworkEvent('webapi_refresh_login_attempt', {})
        
        // Use the new loginWithRefreshToken method that creates a full session
        const result = await this.webapi.loginWithRefreshToken(refreshToken)
        
        if (result.success) {
            SentryClient.trackNetworkEvent('webapi_refresh_login_success', { 
                username: result.profile.username 
            })
            
            this.events.emit('login', {
                success: true,
                username: result.profile.username,
                profile: result.profile,
                sessionKey: result.sessionKey
            })
        } else {
            SentryClient.trackNetworkEvent('webapi_refresh_login_failed', { 
                error: result.error 
            })
        }
        
        return result
    }
    
    /**
     * Connect to game server with WebAPI credentials
     * Uses sessionKey and accessToken from WebAPI auth
     * @param {string} world - World/server name
     */
    connectGameWithWebAPI(world) {
        console.log('[Network] connectGameWithWebAPI called for world:', world)
        console.log('[Network] webapi exists:', !!this.webapi)
        console.log('[Network] webapi.isAuthenticated():', this.webapi?.isAuthenticated())
        
        if (!this.webapi || !this.webapi.isAuthenticated()) {
            console.error('[Network] Cannot connect to game: not authenticated with WebAPI')
            console.error('[Network] webapi:', this.webapi)
            console.error('[Network] accessToken:', this.webapi?.accessToken)
            console.error('[Network] sessionKey:', this.webapi?.sessionKey)
            console.error('[Network] profile:', this.webapi?.profile)
            return
        }
        
        const credentials = this.webapi.getGameCredentials()
        console.log('[Network] Game credentials:', credentials)
        
        if (!credentials) {
            console.error('[Network] Cannot connect to game: missing credentials')
            return
        }
        
        // Store connection data for potential reconnection
        this.connectionData = {
            world: world,
            username: credentials.username,
            sessionKey: credentials.sessionKey,
            accessToken: credentials.accessToken,
            isWebAPI: true
        }
        
        console.log('[Network] Connecting to game server with credentials')
        
        SentryClient.trackNetworkEvent('webapi_game_connection_attempt', {
            world: world,
            username: credentials.username
        })
        
        this.connect(world, () => {
            SentryClient.trackNetworkEvent('webapi_game_connected', { world: world })
            // Send WebAPI auth to game server
            this.send('game_auth', {
                username: credentials.username,
                sessionKey: credentials.sessionKey,
                accessToken: credentials.accessToken,
                isWebAPI: true
            })
            this.worldName = world
            this.isAuthenticated = false // Will be set to true when auth succeeds
        }, () => {
            SentryClient.trackNetworkEvent('webapi_game_disconnected', { world: world })
            this.onConnectionLost()
        })
    }

    connect(world, onConnect, onDisconnect) {
        this.disconnect()

        let config = this.game.crumbs.worlds[world]
        
        // DEVELOPMENT OVERRIDE: Force localhost connection
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            console.log('[Network] DEVELOPMENT MODE: Overriding world config to use localhost')
            config = {
                host: 'http://127.0.0.1:6112',
                path: '/socket.io/'
            }
        }
        
        console.log('[Network] Connecting to world config:', config)

        // Configure transports - WebSocket with polling fallback (Cloudflare compatible)
        const transports = [WebSocket, Fetch]
        const transportOrder = ['websocket', 'polling']

        // Determine if using secure connection based on host URL
        const isSecure = config.host.startsWith('https://')

        SentryClient.trackNetworkEvent('connection_attempt', {
            world: world,
            transportOrder: transportOrder,
            secure: isSecure
        })

        this.client = io.connect(config.host, {
            path: config.path,
            // Custom transport implementations for optimal performance
            transports: transports,
            // Try all transports if the first one fails
            tryAllTransports: true,
            // Upgrade to better transports when available
            upgrade: true,
            // Reconnection settings
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5,
            // Connection timeout
            timeout: 20000,
            // Heartbeat settings
            pingInterval: 25000,
            pingTimeout: 5000,
            // Allow self-signed certificates for development
            rejectUnauthorized: false,
            // Secure connection based on host URL protocol
            secure: isSecure
        })

        console.log(`[Network] Socket.IO connecting to: ${config.host}${config.path}`)
        console.log(`[Network] Transports:`, transportOrder)

        this.client.once('connect', () => {
            console.log(`[Network] ✅ Socket.IO CONNECTED - ID: ${this.client.id}`)
            onConnect()
        })
        this.client.once('disconnect', () => {
            console.log(`[Network] Socket.IO disconnected`)
            onDisconnect()
        })
        this.client.on('connect_error', (error) => {
            console.error(`[Network] ❌ Socket.IO connect_error:`, error.message)
            // Only show connection lost prompt on the first error, not on every reconnection attempt
            if (!this.isShowingReconnectionPrompt && !this.isReconnecting) {
                this.onConnectionLost()
            }
        })
        this.client.on('message', (message) => this.onMessage(message))
        
        // Enhanced reconnection event handlers
        this.client.on('reconnect_attempt', (attemptNumber) => this.onReconnectAttempt(attemptNumber))
        this.client.on('reconnect', (attemptNumber) => this.onReconnect(attemptNumber))
        this.client.on('reconnect_error', (error) => this.onReconnectError(error))
        this.client.on('reconnect_failed', () => this.onReconnectFailed())
        
        // Additional connection monitoring for prompt cleanup
        this.client.on('connect', () => this.onConnected())
        
        // Transport detection and logging
        this.client.on('connect', () => {
            const transport = this.client.io.engine.transport.name
            const upgradeToTransport = this.client.io.engine.upgrading ? 'upgrading' : null
            
            SentryClient.trackNetworkEvent('transport_connected', {
                world: world,
                transport: transport,
                upgrading: upgradeToTransport
            })
        })
        
        // Track transport upgrades
        this.client.io.on('upgrade', (transport) => {
            SentryClient.trackNetworkEvent('transport_upgraded', {
                world: world,
                newTransport: transport.name
            })
        })
        
        // Track transport errors
        this.client.io.on('upgradeError', (error) => {
            SentryClient.trackNetworkEvent('transport_upgrade_failed', {
                world: world,
                error: error.message
            })
        })
        
        // Handle server disconnections (including namespace disconnects)
        this.client.on('disconnect', (reason) => {
            if (reason === 'server namespace disconnect') {
                // Don't trigger normal disconnect handling for this case
                this.onServerDisconnect()
            } else {
                onDisconnect()
            }
        })
    }

    disconnect() {
        if (this.client) {
            this.client.disconnect()
        }
    }

    send(action, args = {}) {
        if (!this.client) {
            console.warn('[Network] send() called but client is null:', action)
            return
        }

        // During reconnection, queue non-auth packets until we're authenticated
        if (!this.isAuthenticated && action !== 'game_auth' && this.worldName && this.worldName !== 'Login') {
            console.log(`[Network] Queueing packet (not authenticated): ${action}`, args)
            this.pendingPackets.push({ action, args })
            return
        }

        console.log(`[Network] Sending packet: ${action}`, args)
        this.client.emit('message', { action: action, args: args })
    }

    // Handlers

    onMessage(message) {
        this.handler.handle(message)
    }

    onConnectionLost() {
        // Don't disconnect immediately - let Socket.IO handle reconnection
        
        console.log('[Network] Connection lost')
        
        SentryClient.trackNetworkEvent('connection_lost', {
            world: this.worldName,
            isAuthenticated: this.isAuthenticated
        })
        
        // Clean up current room scenes to prevent duplicate key errors on reconnection
        this.cleanupRoomScenes()
        
        // Reset authenticated state but keep hasConnectedBefore to detect reconnection
        this.isAuthenticated = false
        
        // Only show prompt if we have an interface controller and aren't already showing one
        try {
            let prompt = this.game.scene.getScene('InterfaceController')?.prompt
            if (prompt && !this.isShowingReconnectionPrompt) {
                this.isShowingReconnectionPrompt = true
                prompt.showError('Connection lost. Attempting to reconnect...', 'Reload Page', () => { window.location.reload() })
            }
        } catch (error) {
            SentryClient.captureException(error, {
                operation: 'connection_lost_prompt',
                world: this.worldName
            })
            // Silently handle prompt error
        }
    }

    onServerDisconnect() {
        // Clean up room scenes before triggering normal disconnect handling
        this.cleanupRoomScenes()
        this.onConnectionLost()
    }

    cleanupRoomScenes() {
        
        try {
            const worldController = this.game.scene.getScene('WorldController')
            if (worldController && worldController.rooms && worldController.rooms.room) {
                worldController.rooms.removeCurrent()
            }
        } catch (error) {
            
            // Fallback: try to manually remove any room scenes
            try {
                const sceneManager = this.game.scene
                const roomKeys = [
                    'Town', 'Village', 'Plaza', 'Beach', 'Dock', 'Coffee', 'Dance', 'Gift',
                    'Lodge', 'Mtn', 'Forest', 'Dojo', 'DojoExt', 'Sport', 'Shop', 'Rink',
                    'Forts', 'Shack', 'Pet', 'Lounge', 'Cove', 'Mine', 'Stadium'
                ] // Common room scene keys
                
                roomKeys.forEach(key => {
                    if (sceneManager.get(key)) {
                        sceneManager.remove(key)
                    }
                })
            } catch (fallbackError) {
                // Silent fallback failure
            }
        }
    }

    onReconnectAttempt(attemptNumber) {
        
        try {
            let prompt = this.game.scene.getScene('InterfaceController')?.prompt
            if (prompt) {
                // Close existing error prompt before showing updated one
                if (prompt.error.visible) {
                    prompt.error.close()
                }
                prompt.showError(`Reconnecting... (${attemptNumber}/5)`, 'Reload Page', () => { window.location.reload() })
            }
        } catch (error) {
            // Silent error handling
        }
    }

    onReconnect(attemptNumber) {
        
        console.log(`[Network] Reconnected successfully after ${attemptNumber} attempt(s)`)
        
        // Mark that we successfully reconnected - will be used by onConnected
        this.isReconnecting = true
        
        // Reset reconnection prompt tracking
        this.isShowingReconnectionPrompt = false
        
        try {
            let prompt = this.game.scene.getScene('InterfaceController')?.prompt
            if (prompt) {
                // Close any existing prompts
                if (prompt.error && prompt.error.visible) {
                    prompt.error.close()
                }
                if (prompt.window && prompt.window.visible) {
                    prompt.window.close()
                }
            }
        } catch (error) {
            // Silent error handling
        }
        
        // Re-authentication will happen in onConnected, which will trigger room join
    }

    onReconnectError(error) {
        SentryClient.captureException(error, {
            operation: 'reconnection_error',
            world: this.worldName
        })
        // Silent error handling
    }

    onConnected() {
        
        console.log('[Network] onConnected called, isReconnecting:', this.isReconnecting, 'hasConnectedBefore:', this.hasConnectedBefore)
        
        // Detect reconnection: either the reconnect event fired OR we've connected before and have connection data
        const isActualReconnection = this.isReconnecting || (this.hasConnectedBefore && this.connectionData && this.worldName && this.worldName !== 'Login')
        
        // If this is a reconnection, re-authenticate
        if (isActualReconnection) {
            this.isReconnecting = false // Reset flag
            this.cleanupReconnectionPrompts()
            
            // This is a reconnection - re-authenticate immediately
            if (this.worldName && this.worldName !== 'Login' && this.connectionData) {
                console.log('[Network] Reconnection detected, re-authenticating...')
                this.isAuthenticated = false
                this.reAuthenticateGameServer()
                return
            }
        }
        
        // Mark that we've now connected at least once
        this.hasConnectedBefore = true
        
        // For login server, set authenticated immediately
        if (this.worldName === 'Login') {
            this.isAuthenticated = true
        }
    }

    cleanupReconnectionPrompts() {
        this.isShowingReconnectionPrompt = false
        
        try {
            let prompt = this.game.scene.getScene('InterfaceController')?.prompt
            if (prompt) {
                // Close any existing prompts
                if (prompt.error && prompt.error.visible) {
                    prompt.error.close()
                }
                if (prompt.window && prompt.window.visible) {
                    prompt.window.close()
                }
            }
        } catch (error) {
            // Silent error handling
        }
    }

    onReconnectFailed() {
        SentryClient.trackNetworkEvent('reconnection_failed', {
            world: this.worldName,
            attempts: 5
        })
        
        // Reset reconnection prompt tracking
        this.isShowingReconnectionPrompt = false
        
        // Only now disconnect and show final error
        this.disconnect()
        
        try {
            let prompt = this.game.scene.getScene('InterfaceController')?.prompt
            if (prompt) {
                // Close any existing prompts
                if (prompt.error.visible) {
                    prompt.error.close()
                }
                if (prompt.window.visible) {
                    prompt.window.close()
                }
                
                // Show final error with reload option
                setTimeout(() => {
                    prompt.showError('Connection could not be restored.\\nPlease refresh the page.', 'Refresh', () => {
                        window.location.reload()
                    })
                }, 100)
            }
        } catch (error) {
            SentryClient.captureException(error, {
                operation: 'reconnection_failed_prompt',
                world: this.worldName
            })
            // Fallback: just reload the page
            window.location.reload()
        }
    }

    // Saved penguins

    get isSavedPenguins() {
        if (localStorage.getItem('saved_penguins')) {
            return true
        } else {
            return false
        }
    }

    get savedPenguins() {
        let savedPenguins = localStorage.getItem('saved_penguins')

        if (!savedPenguins) {
            return {}
        }

        try {
            const parsed = JSON.parse(savedPenguins)
            // COPPA/GDPR-K Compliance (Finding S5): Filter expired penguins (90 days)
            const now = Date.now()
            const filtered = {}
            let hasExpired = false
            
            for (const [key, penguin] of Object.entries(parsed)) {
                // Check if penguin has expiry and if it's still valid
                if (penguin.expiresAt && penguin.expiresAt < now) {
                    hasExpired = true
                    continue // Skip expired penguins
                }
                // Add expiry if missing (for backwards compatibility)
                if (!penguin.expiresAt) {
                    penguin.savedAt = penguin.savedAt || now
                    penguin.expiresAt = now + (90 * 24 * 60 * 60 * 1000) // 90 days
                }
                filtered[key] = penguin
            }
            
            // Clean up expired penguins from storage
            if (hasExpired) {
                localStorage.setItem('saved_penguins', JSON.stringify(filtered))
            }
            
            return filtered
        } catch (error) {
            return {}
        }
    }

    getToken(username) {
        let save = this.savedPenguins[username.toLowerCase()]

        if (save && save.token) {
            // Check if token is encrypted and decrypt it
            if (isEncryptedToken(save.token)) {
                // Return a promise that resolves to the decrypted token
                // For synchronous compatibility, cache the decrypted token
                if (save._decryptedToken) {
                    return save._decryptedToken
                }
                // Decrypt asynchronously and cache
                decryptToken(save.token).then(decrypted => {
                    save._decryptedToken = decrypted
                })
                // Return the encrypted token for now, will be decrypted on next call
                return save.token
            }
            return save.token
        } else {
            return null
        }
    }
    
    /**
     * Get token asynchronously (preferred for encrypted tokens)
     */
    async getTokenAsync(username) {
        let save = this.savedPenguins[username.toLowerCase()]
        
        if (save && save.token) {
            if (isEncryptedToken(save.token)) {
                return await decryptToken(save.token)
            }
            return save.token
        }
        return null
    }
    
    /**
     * Save a token with encryption
     * COPPA/GDPR-K Compliance (Finding S5): Adds 90-day expiry to saved penguins
     */
    async saveToken(username, token, penguinData = {}) {
        if (!token) return
        
        try {
            const encryptedToken = await encryptToken(token)
            if (!encryptedToken) {
                console.warn('[Network] Token encryption failed, not saving token')
                return
            }
            
            const now = Date.now()
            let savedPenguins = this.savedPenguins
            savedPenguins[username.toLowerCase()] = {
                ...penguinData,
                username: username,
                token: encryptedToken,
                // Finding S5: Add expiry timestamps for storage limitation compliance
                savedAt: now,
                expiresAt: now + (90 * 24 * 60 * 60 * 1000) // 90 days
            }
            localStorage.setItem('saved_penguins', JSON.stringify(savedPenguins))
        } catch (error) {
            console.error('[Network] Error saving encrypted token:', error)
        }
    }



    reAuthenticateGameServer() {
        console.log('[Network] reAuthenticateGameServer called')
        
        if (!this.connectionData) {
            console.error('[Network] Cannot re-authenticate: no connection data stored')
            return
        }
        
        try {
            // Use stored connection data to re-authenticate with WebAPI credentials
            const { username, sessionKey, accessToken, isWebAPI } = this.connectionData
            
            let response = { 
                username: username,
                sessionKey: sessionKey,
                accessToken: accessToken,
                isWebAPI: isWebAPI,
                isReconnection: true
            }
            
            console.log('[Network] Sending game_auth for reconnection:', { username, isWebAPI, isReconnection: true })
            this.send('game_auth', response)
            
        } catch (error) {
            console.error('[Network] Error during re-authentication:', error)
            SentryClient.captureException(error, {
                operation: 'reauthentication_failed',
                world: this.worldName
            })
        }
    }

    onAuthenticationSuccess() {
        console.log('[Network] onAuthenticationSuccess called')
        this.isAuthenticated = true
        
        // Send any queued packets
        const packets = [...this.pendingPackets]
        this.pendingPackets = []
        
        console.log(`[Network] Sending ${packets.length} queued packets`)
        packets.forEach(packet => {
            console.log(`[Network] Sending queued packet: ${packet.action}`, packet.args)
            this.send(packet.action, packet.args)
        })
    }

    onServerDisconnect() {
        
        // Reset reconnection state
        this.isShowingReconnectionPrompt = false
        
        // Show specific message for server disconnects
        try {
            let prompt = this.game.scene.getScene('InterfaceController')?.prompt
            if (prompt) {
                // Close any existing prompts
                if (prompt.error && prompt.error.visible) {
                    prompt.error.close()
                }
                if (prompt.window && prompt.window.visible) {
                    prompt.window.close()
                }
                
                prompt.showError('Session expired or duplicate connection detected.\\nPlease refresh to reconnect.', 'Refresh', () => {
                    window.location.reload()
                })
            }
        } catch (error) {
            // Fallback: just reload the page
            setTimeout(() => window.location.reload(), 2000)
        }
    }

}
